/* Copyright (c) 2020-present CloudIO Inc */

import {
  caseOppositeOperator,
  DataRecord,
  isOpCaseInsensitive,
  MULTIPLE_STRING_OPS,
  OPS_ICONS,
  STRING_OPS_KEY_TYPE,
} from '@cloudio-saas/datasource-types';
import { useEvent } from '@cloudio-saas/statex';
import type { TextFieldProps as MUITextFieldProps } from '@mui/material';
import {
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField as MUITextField,
} from '@mui/material';
import { BaseProps, Form, SearchTextField } from 'componentregistry';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { allStringOps, hasTextEditor } from 'components/SmartSearch/Operators';
import {
  getName,
  getOperator,
  getOpOptions,
  getValue,
} from 'components/SmartSearch/SearchUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import { useFocus } from 'hooks/useFocus';
import Icon from 'icons/Icon';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useStoreAttributeFilter } from 'store/useAppStore';
import { isNotEmpty } from 'utils/isEmpty';
import { useTranslate } from '../../utils/lang';
import { useStringExpression } from 'expression/useExpression';
import zIndex from 'constants/ZIndex';

export type TextInputType = 'text' | 'password';

interface TextFieldProps
  extends Required<SearchTextField.SearchTextField>,
    BaseProps<'SearchTextField'> {}

interface Props extends TextFieldProps, Form.HorizontalFormProps {
  headerFilter?: boolean;
  onHeaderFilterSearch?: () => void;
}

function IOSearchTextField(
  props: Props,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement {
  const {
    appUid,
    autoFocus,
    children,
    className,
    defaultOperator,
    domProps,
    flex,
    formHorizontal,
    hideOperatorChooser,
    itemId,
    labelAlignment,
    labelWidth,
    required,
    viewAttribute,
    platform,
    headerFilter,
    pageId,
    onHeaderFilterSearch,
  } = props;

  let { helperText, placeholder } = props;
  const { label } = props;
  const { t } = useTranslate();
  helperText = t(helperText);
  let labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;
  labelResult = t(labelResult);
  placeholder = t(placeholder);
  const variant = useFieldVariant(appUid);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (platform.isRecording()) {
      platform.recordLine(
        itemId,
        `await ui.click({ testid: '${itemId}-op' });`,
      );
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [ops, setOps] = useState<STRING_OPS_KEY_TYPE>(defaultOperator);
  const [value, setValue] = useState('');
  const [caseInSensitive, setCaseInSensitive] = useState(
    isOpCaseInsensitive(defaultOperator),
  );
  const options = getOpOptions('Text', t, caseInSensitive);

  const focusedValue = useRef(value);
  const currentValue = useRef(value);

  useEffect(() => {
    currentValue.current = value;
  }, [value]);
  const isFocused = useRef(false);

  const store = useCurrentDataStore<DataRecord>();
  const filter = useStoreAttributeFilter<DataRecord>(store, viewAttribute);

  useEffect(() => {
    if (filter) {
      const op = getOperator(filter) as STRING_OPS_KEY_TYPE;
      setOps(op);
      setCaseInSensitive(isOpCaseInsensitive(op));
      let val = getValue(filter) as string | string[];
      if (Array.isArray(val)) {
        val = val.join(', ');
      }
      setValue(val);
      if (headerFilter && focusedValue.current === '') {
        // on header filter, the focus is set before useEffect is called
        // so we need to set the focusedValue here
        focusedValue.current = val;
      }
    } else {
      // #974 Fix. Empty/Not Empty filter has to be set to default operator
      setOps(defaultOperator);
      setValue('');
    }
  }, [defaultOperator, filter, headerFilter, viewAttribute]);

  const hasEditor = hasTextEditor(ops);

  function updateFilter(attr: string, op: STRING_OPS_KEY_TYPE, val: string) {
    if (!store) return;

    let filters = store.getSmartSearchFilter();
    filters = filters.filter((f) => getName(f) !== attr);
    const _hasEditor = hasTextEditor(op);
    if (!_hasEditor || isNotEmpty(val)) {
      let v: string | string[] = _hasEditor ? val : '';
      if (op in MULTIPLE_STRING_OPS) {
        v = val
          .split(',')
          .map((_v) => _v.trim())
          .filter(isNotEmpty);
      }
      const _filter = {
        [attr]: { [op]: v },
      };
      filters.push(_filter);
    } else {
      setOps(op);
      setValue('');
    }

    store?.setSmartSeachFilter(filters);
  }

  const onBlur = useEvent(async () => {
    isFocused.current = false;
    if (focusedValue.current !== currentValue.current) {
      focusedValue.current = currentValue.current;
      updateFilter(viewAttribute, ops, currentValue.current);
    }
  });
  useEffect(() => {
    return () => {
      if (isFocused.current) onBlur();
    };
  }, [onBlur]);
  const inputRef = useFocus(itemId);

  if (ops in MULTIPLE_STRING_OPS) {
    placeholder = `e.g. a, b, c${placeholder ? ` (${placeholder})` : ''}`;
  }

  const id = useId();
  const textFieldProps: MUITextFieldProps = {
    id,
    fullWidth: true,
    autoFocus,
    helperText,
    autoComplete: 'off',
    required,
    variant,
    value: value ?? '',
    onChange: async (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      currentValue.current = newValue;
      setValue(newValue);
      if (platform.isRecording() && !headerFilter) {
        platform.recordLine(
          itemId,
          `await ui.type('${itemId}', '${newValue}');`,
        );
      }
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onBlur();
        onHeaderFilterSearch?.();
      }
    },
    type: 'text',
    InputProps: {
      // disableUnderline: headerFilter,
      onFocus: async (event: React.FocusEvent<HTMLInputElement>) => {
        isFocused.current = true;
        focusedValue.current = value;
      },
      onBlur,
      inputRef,
      startAdornment: hideOperatorChooser ? undefined : (
        <InputAdornment position="start">
          <IconButton
            data-testid={`${itemId}-op`}
            onClick={handleClick}
            data-tip={allStringOps[ops] || ops}
          >
            <span aria-hidden="false" style={{ display: 'none' }}>
              {allStringOps[ops] || ops}
            </span>
            <Icon iconName={OPS_ICONS[ops] || 'equals'} />
          </IconButton>
          {hasEditor ? (
            <Icon
              iconName="font-case"
              onClick={() => {
                setCaseInSensitive(!caseInSensitive);
                const op = caseOppositeOperator(ops);
                setOps(op);
                updateFilter(viewAttribute, op, value);
                if (hasTextEditor(op)) {
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 1);
                }
              }}
              tooltip={caseInSensitive ? 'Ignore Case' : 'Match Case'}
              color={caseInSensitive ? 'default' : 'primary'}
            />
          ) : null}
        </InputAdornment>
      ),
    },
    placeholder: hasEditor ? placeholder : allStringOps[ops],
    disabled: !hasEditor,
  };

  labelResult =
    ops === 'is' ? labelResult : `${labelResult} (${allStringOps[ops]})`;

  return (
    <>
      <FieldWrapper
        className={className}
        domProps={domProps}
        flex={flex}
        ref={ref}
      >
        {formHorizontal && !headerFilter ? (
          <HorizontalFormField
            control={
              <MUITextField {...textFieldProps} hiddenLabel size="small" />
            }
            label={labelResult}
            labelAlignment={labelAlignment}
            labelWidth={labelWidth}
            required={required}
            id={id}
          />
        ) : (
          <MUITextField
            {...textFieldProps}
            label={headerFilter ? undefined : labelResult}
            hiddenLabel={headerFilter}
          />
        )}
        {children}
      </FieldWrapper>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={{ zIndex: zIndex.Tooltip + 1 }}
      >
        {options.map((option) => {
          return (
            <MenuItem
              data-testid={`op-${option.value}`}
              key={option.value}
              onClick={() => {
                const op = option.value as STRING_OPS_KEY_TYPE;
                setOps(op);
                handleClose();
                updateFilter(viewAttribute, op, value);
                if (hasTextEditor(op)) {
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 1);
                } else {
                  onHeaderFilterSearch?.();
                }
              }}
            >
              <ListItemIcon>
                <Icon iconName={OPS_ICONS[option.value] || 'equals'} />
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default React.forwardRef(IOSearchTextField);
